import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { motion, AnimatePresence, Variants, useReducedMotion } from 'framer-motion';
import { ModifierGroup } from '../product-details/ModifierGroup';
import { SPICE_LEVELS, shouldShowSpiceLevel, DEFAULT_SPICE_LEVEL } from '../../utils/spiceUtils';
import {
  filterEnabledModifiers,
  isModifierRequired,
  handleModifierOptionSelection,
  isModifierOptionSelected,
  handleSubOptionSelection,
} from '../../utils/modifierUtils';
import { parsePrice, formatPrice } from '../../utils/priceUtils';

// Animation variants
const overlayVariants: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

const modalVariants: Variants = {
  hidden: { opacity: 0, scale: 0.95, y: 20 },
  visible: { opacity: 1, scale: 1, y: 0 },
  exit: { opacity: 0, scale: 0.95, y: 20 },
};

const springTransition = {
  type: 'spring' as const,
  stiffness: 300,
  damping: 30,
};

interface ItemDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: any;
  onAddToOrder: (orderItem: {
    name: string;
    quantity: string;
    itemPrice: string;
    modifiers: Array<{
      modifier_name: string;
      modifier_price: string;
    }>;
    spiceLevel?: number;
  }) => void;
}

export const ItemDetailsModal: React.FC<ItemDetailsModalProps> = ({
  isOpen,
  onClose,
  item,
  onAddToOrder,
}) => {
  const shouldReduceMotion = useReducedMotion();
  const animationTransition = shouldReduceMotion ? { duration: 0 } : springTransition;

  const [quantity, setQuantity] = useState(1);
  const [selectedModifiers, setSelectedModifiers] = useState<any[]>([]);
  const [expandedModifiers, setExpandedModifiers] = useState<Set<string>>(new Set());
  const [spiceLevel, setSpiceLevel] = useState(DEFAULT_SPICE_LEVEL);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Get enabled modifiers
  const enabledModifiers = useMemo(() => {
    return filterEnabledModifiers(item?.modifierList || []);
  }, [item]);

  // Get required modifiers for auto-expand
  const requiredModifiers = useMemo(() => {
    return enabledModifiers
      .filter(mod => isModifierRequired(mod))
      .map(mod => mod.name);
  }, [enabledModifiers]);

  // Calculate total price
  const totalPrice = useMemo(() => {
    const basePrice = parsePrice(item?.price || '0');
    
    let modifiersTotal = 0;
    selectedModifiers.forEach(modifier => {
      modifier.options.forEach((option: any) => {
        const price = option.subOption?.price || option.price;
        modifiersTotal += parsePrice(price);
      });
    });

    return (basePrice + modifiersTotal) * quantity;
  }, [item, selectedModifiers, quantity]);

  // Reset when modal opens
  useEffect(() => {
    if (isOpen && item) {
      setQuantity(1);
      setSpiceLevel(DEFAULT_SPICE_LEVEL);
      setSelectedModifiers([]);
      setValidationErrors({});
      setExpandedModifiers(new Set(requiredModifiers));
    }
  }, [isOpen, item, requiredModifiers]);

  // ESC key close
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'hidden';
      return () => {
        document.removeEventListener('keydown', handleEscKey);
        document.body.style.overflow = '';
      };
    }
  }, [isOpen, onClose]);

  // Toggle modifier expansion
  const toggleModifier = useCallback((modifierName: string) => {
    setExpandedModifiers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(modifierName)) {
        newSet.delete(modifierName);
      } else {
        newSet.add(modifierName);
      }
      return newSet;
    });
  }, []);

  // Handle modifier option selection
  const handleOptionSelect = useCallback((modifierName: string, option: any) => {
    const modifier = enabledModifiers.find(m => m.name === modifierName);
    if (!modifier) return;

    const newModifiers = handleModifierOptionSelection(
      modifierName,
      option,
      selectedModifiers,
      enabledModifiers
    );
    setSelectedModifiers(newModifiers);

    if (validationErrors[modifierName]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[modifierName];
        return newErrors;
      });
    }
  }, [enabledModifiers, selectedModifiers, validationErrors]);

  // Handle sub-option selection
  const handleSubOptionSelect = useCallback((
    modifierName: string,
    optionName: string,
    subSelection: string,
    subPrice: string
  ) => {
    const newModifiers = handleSubOptionSelection(
      modifierName,
      optionName,
      subSelection,
      subPrice,
      selectedModifiers
    );
    setSelectedModifiers(newModifiers);
  }, [selectedModifiers]);

  // Validate required modifiers
  const validateModifiers = useCallback(() => {
    const errors: Record<string, string> = {};
    
    enabledModifiers.forEach(modifier => {
      if (isModifierRequired(modifier)) {
        const hasSelection = selectedModifiers.some(m => m.name === modifier.name);
        if (!hasSelection) {
          errors[modifier.name] = `Please select a ${modifier.name.toLowerCase()}`;
        }
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [enabledModifiers, selectedModifiers]);

  // Handle add to order
  const handleAddToOrder = useCallback(() => {
    if (!validateModifiers()) {
      return;
    }

    const formattedModifiers: Array<{ modifier_name: string; modifier_price: string }> = [];
    selectedModifiers.forEach(modifier => {
      modifier.options.forEach((option: any) => {
        const price = option.subOption?.price || option.price;
        const name = option.subOption?.selection 
          ? `${option.name} (${option.subOption.selection})`
          : option.name;
        
        formattedModifiers.push({
          modifier_name: name,
          modifier_price: parsePrice(price).toFixed(2),
        });
      });
    });

    const orderItem = {
      name: item.name,
      quantity: quantity.toString(),
      itemPrice: parsePrice(item.price).toFixed(2),
      modifiers: formattedModifiers,
    };

    onAddToOrder(orderItem);
    onClose();
    
    setQuantity(1);
    setSelectedModifiers([]);
    setExpandedModifiers(new Set());
    setSpiceLevel(DEFAULT_SPICE_LEVEL);
    setValidationErrors({});
  }, [item, quantity, selectedModifiers, spiceLevel, validateModifiers, onAddToOrder, onClose]);

  if (!isOpen || !item) return null;

  return (
    <AnimatePresence>
      <motion.div
        variants={overlayVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        transition={{ duration: 0.2 }}
        className="fixed inset-0 z-[60] backdrop-blur-sm bg-black/60 flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        onClick={onClose}
      >
        <motion.div
          variants={modalVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          transition={animationTransition}
          className="bg-[var(--bg-primary)] rounded-lg border border-[var(--border-color)] max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header - Compact */}
          <div className="flex items-center justify-between p-3 border-b border-[var(--border-color)] bg-[var(--bg-secondary)]">
            <div className="flex-1">
              <h3 className="text-base font-bold text-[var(--text-primary)]">{item.name}</h3>
              {item.description && (
                <p className="text-xs text-[var(--text-secondary)] mt-0.5 line-clamp-2">{item.description}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className="ml-3 w-7 h-7 flex items-center justify-center text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Body - Ultra Compact */}
          <div ref={scrollContainerRef} className="flex-1 overflow-y-auto p-2">
            {/* Modifiers - Ultra Compact */}
            {enabledModifiers.length > 0 && (
              <div className="mb-3">
                <h4 className="text-xs font-semibold text-[var(--text-primary)] mb-1.5">Customize Your Order</h4>
                {enabledModifiers.map(modifier => (
                  <ModifierGroup
                    key={modifier.name}
                    modifier={modifier}
                    isExpanded={expandedModifiers.has(modifier.name)}
                    onToggle={() => toggleModifier(modifier.name)}
                    onOptionSelect={(option) => handleOptionSelect(modifier.name, option)}
                    isOptionSelected={(optionName) =>
                      isModifierOptionSelected(modifier.name, optionName, selectedModifiers)
                    }
                    validationError={validationErrors[modifier.name]}
                    onSubOptionSelect={handleSubOptionSelect}
                    selectedModifiers={selectedModifiers}
                  />
                ))}
              </div>
            )}


            {/* Quantity - Ultra Compact */}
            <div className="mb-2">
              <h4 className="text-xs font-semibold text-[var(--text-primary)] mb-1.5">Quantity</h4>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-7 h-7 flex items-center justify-center rounded border border-gray-300 hover:bg-gray-50"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                  </svg>
                </button>
                <span className="text-sm font-semibold min-w-[2.5ch] text-center">{quantity}</span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="w-7 h-7 flex items-center justify-center rounded border border-gray-300 hover:bg-gray-50"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Footer - Compact */}
          <div className="p-3 border-t border-[var(--border-color)] bg-[var(--bg-secondary)]">
            <button
              onClick={handleAddToOrder}
              className="w-full px-4 py-2 bg-[var(--accent-color)] text-white rounded-lg text-sm font-semibold hover:opacity-90 transition-opacity"
            >
              Add • ${totalPrice.toFixed(2)}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
